/**
 * The manager's board for one onboarding (PRD 10.3): a lane per department,
 * every card in it in the order the work flows, what is late and what is
 * held by somebody who has gone. A card opens on its own screen, where
 * reassigning and skipping live. HR and admins can move the start date or
 * cancel the whole onboarding from here.
 *
 * What each card shows is still `cardFor()`'s decision: a member of another
 * department sees titles and states, a manager sees whole cards.
 */

import { useEffect, useState, type FormEvent } from "react";
import type { BoardCard, BoardResponse } from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { auditOf, boardOf, go, summaryOf } from "../lib/router.js";
import { anchorLabel, dayText, dueText, kindWord, plural } from "../lib/words.js";
import { Link, Loading, PageHeader, Problem, SlaPill, StatePill, useLoad } from "./bits.js";
import { Icon } from "./Icon.js";
import { Progress } from "./Onboardings.js";

export function Board({ id, kind }: { id: string; kind?: "offboarding" | undefined }) {
  const { api, nameOf } = useOrg();
  const { data: board, setData, error } = useLoad(() => api.board(id), [api, id]);

  // An offboarding reached at its old /onboardings address moves to its own, so the right tab is lit.
  const actual = board?.instance.id === id ? board.instance.kind : null;
  useEffect(() => {
    if (actual && (actual === "offboarding") !== (kind === "offboarding")) go(boardOf(id, actual), true);
  }, [actual, kind, id]);

  if (!board && !error) return <Loading title="Board" />;
  if (!board) {
    return (
      <section className="page">
        <PageHeader title="Board" />
        <Problem>{error}</Problem>
      </section>
    );
  }

  const { instance } = board;
  const lanes = new Map<string, BoardCard[]>();
  for (const entry of board.cards)
    lanes.set(entry.card.department, [...(lanes.get(entry.card.department) ?? []), entry]);

  return (
    <section className="page">
      <PageHeader
        back={
          instance.kind === "offboarding"
            ? { to: { name: "offboardings" }, label: "Offboarding" }
            : { to: { name: "onboardings" }, label: "Onboarding" }
        }
        title={<Link to={{ name: "employee", id: instance.employeeId }}>{instance.employeeName}</Link>}
        meta={
          <>
            <span>
              {instance.kind === "offboarding" ? anchorLabel(instance) : "Starts"}{" "}
              {dayText(instance.anchorDate, true)}
            </span>
            <span className="chip">{instance.siteName}</span>
            <span>{instance.templateName}</span>
          </>
        }
        actions={
          <>
            <Link to={summaryOf(instance.id, instance.kind)} className="button button-quiet">
              <Icon name="inbox" size={18} />
              Export as PDF
            </Link>
            {board.canReadAudit ? (
              <Link to={auditOf(instance.id, instance.kind)} className="button button-quiet">
                <Icon name="clipboard" size={18} />
                Audit log
              </Link>
            ) : null}
          </>
        }
      />
      <div className="panel">
        {/* Every card of a cancelled one is closed; a full bar would say it was done. */}
        {instance.cancelled ? null : (
          <div className="board-progress">
            <Progress instance={instance} />
            <span className="muted">
              {instance.finished} of {plural(instance.total, "card")} finished
            </span>
          </div>
        )}
        <p className="card-meta">
          {instance.overdue > 0 ? <span className="pill s-danger">{instance.overdue} late</span> : null}
          {instance.flagged > 0 ? (
            <span className="pill s-warn">{plural(instance.flagged, "card")} held by somebody disabled</span>
          ) : null}
          {instance.closedAt && !instance.cancelled ? <span className="pill s-sent">Finished</span> : null}
        </p>
        {instance.cancelled ? (
          <p className="notice-line">
            <strong>Cancelled</strong> by {nameOf(instance.cancelled.by)}: {instance.cancelled.reason}
          </p>
        ) : null}
        {board.canChange && !instance.closedAt ? <Change board={board} onBoard={setData} /> : null}
      </div>

      <div className="lanes">
        {[...lanes.entries()].map(([department, entries]) => {
          const done = entries.filter((entry) => ["completed", "skipped"].includes(entry.card.state)).length;
          return (
            <section key={department} className="panel lane" aria-labelledby={`lane-${department}`}>
              <h2 id={`lane-${department}`} className="lane-title">
                {department}
                <span className="zone-count">
                  {done}/{entries.length}
                </span>
              </h2>
              <ol className="lane-cards">
                {entries.map((entry) => {
                  const { card } = entry;
                  const later =
                    card.state === "not_started" || card.state === "completed" || card.state === "skipped";
                  return (
                    <li key={card.key}>
                      <Link
                        to={{ name: "card", id: instance.id, key: card.key }}
                        className={`card card-link card-small${later ? " card-later" : ""}`}
                      >
                        <div className="card-top">
                          <h3 className="card-title">
                            {card.kind === "notify" ? <span className="card-kind">Notice · </span> : null}
                            {card.title}
                          </h3>
                        </div>
                        <p className="card-meta">
                          <StatePill state={card.state} />
                          <SlaPill sla={entry.sla} />
                          {card.full && card.kind === "task" ? <span>{nameOf(card.assigneeId)}</span> : null}
                          {card.full && card.state !== "completed" && card.state !== "skipped" ? (
                            <span>{dueText(card)}</span>
                          ) : null}
                          {entry.flagged ? <span className="pill s-warn">Hand over</span> : null}
                        </p>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Moving the start date and cancelling: both change every card at once, so
 * each asks first, says what will happen, and answers with the board as it
 * was stored.
 */
function Change({ board, onBoard }: { board: BoardResponse; onBoard: (board: BoardResponse) => void }) {
  const { api, refreshUnread } = useOrg();
  const { instance } = board;
  const what = kindWord(instance);
  const anchor = anchorLabel(instance);
  const [asking, setAsking] = useState<null | "move" | "cancel">(null);
  const [day, setDay] = useState(instance.anchorDate);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const ask = (which: "move" | "cancel" | null) => {
    setAsking(which);
    setProblem(null);
    setReason("");
    setDay(instance.anchorDate);
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      onBoard(
        asking === "move"
          ? await api.move(instance.id, { anchorDate: day, reason })
          : await api.cancel(instance.id, { reason }),
      );
      void refreshUnread();
      ask(null);
    } catch (error) {
      setProblem(sentence(error));
    } finally {
      setBusy(false);
    }
  }

  if (asking === null) {
    return (
      <div className="action-row">
        <button className="button button-quiet" type="button" onClick={() => ask("move")}>
          Move {anchor.toLowerCase()}
        </button>
        <button className="button button-quiet" type="button" onClick={() => ask("cancel")}>
          Cancel {what}
        </button>
      </div>
    );
  }

  const moving = asking === "move";
  return (
    <form className="ask" onSubmit={submit}>
      <p className="muted">
        {moving
          ? `Every deadline counted from the ${anchor.toLowerCase()} moves with it, and whoever holds an open card is told.`
          : `Every card not finished closes with this reason, and everybody holding one is told. It cannot be undone; if you need it again, start a new ${what}.`}
      </p>
      {moving ? (
        <label className="field">
          <span>New {anchor.toLowerCase()}</span>
          <input
            type="date"
            value={day}
            onChange={(event) => event.target.value && setDay(event.target.value)}
          />
        </label>
      ) : null}
      <label className="field">
        <span>
          {moving
            ? "Why? Optional; it goes in the audit log."
            : "Why? It goes on every card and in the audit log."}
        </span>
        <textarea
          value={reason}
          rows={2}
          onChange={(event) => setReason(event.target.value)}
          placeholder={moving ? "Their visa came through a week late." : "Offer withdrawn."}
        />
      </label>
      <Problem>{problem}</Problem>
      <div className="action-row">
        <button
          className="button"
          type="submit"
          disabled={busy || (moving ? day === instance.anchorDate : !reason.trim())}
        >
          {moving ? "Move it" : `Cancel the ${what}`}
        </button>
        <button className="button button-quiet" type="button" onClick={() => ask(null)}>
          Keep it as it is
        </button>
      </div>
    </form>
  );
}
